import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * ジャンプボタンの種別を表す定数
 */
export const JUMP_BUTTON_TYPES = {
  DISEASE: 'disease',    // 該当傷病
  CONTENTS: 'contents',  // 目次
  LEGEND: 'legend',      // 凡例
  APPENDIX: 'appendix',  // 別表
};

/**
 * ジャンプボタングループを管理するカスタムフック
 * 
 * PDFビューア内の該当傷病、目次、凡例、別表へのジャンプ機能を提供する。
 * 各ボタンのアクティブ状態とジャンプ可否を管理し、クリック時のハンドラを返す。
 * 
 * @param {Object} options - オプション
 * @param {Object} options.detailResult - 検索結果の詳細データ（各セクションのページ番号を含む）
 * @param {Function} options.jumpToPage - 指定ページへジャンプする関数
 * @param {Function} options.isJumpPageValid - ジャンプ可否を判定する関数
 * @returns {Object} ジャンプボタン関連の状態とハンドラ
 * @returns {string} returns.activeJumpButton - 現在アクティブなボタン種別（'disease' | 'contents' | 'legend' | 'appendix'）
 * @returns {boolean} returns.canJumpDisease - 該当傷病へジャンプ可能か
 * @returns {boolean} returns.canJumpContents - 目次へジャンプ可能か
 * @returns {boolean} returns.canJumpLegend - 凡例へジャンプ可能か
 * @returns {boolean} returns.canJumpAppendix - 別表へジャンプ可能か
 * @returns {Function} returns.handleJumpDisease - 該当傷病へジャンプするハンドラ
 * @returns {Function} returns.handleJumpContents - 目次へジャンプするハンドラ
 * @returns {Function} returns.handleJumpLegends - 凡例へジャンプするハンドラ
 * @returns {Function} returns.handleJumpAppendix - 別表へジャンプするハンドラ
 */
export const useJumpButtonGroup = ({
  detailResult,
  jumpToPage,
  isJumpPageValid,
  activePairKey,
  currentPage,
}) => {
  /**
   * 現在アクティブなジャンプボタン
   * JUMP_BUTTON_TYPES の値を使用
   */
  const [activeJumpButton, setActiveJumpButtonState] = useState(JUMP_BUTTON_TYPES.DISEASE);

  /**
   * 会社・傷病ペアごとのジャンプ先ページ状態
   * key: ペアキー、value: ボタン種別ごとの最後に表示していたページ番号
   */
  const jumpPageStateByPairRef = useRef({});

  /**
   * 会社・傷病ペアごとのジャンプ実行済み状態
   * 手動ページ移動を保存対象にするか判定するために使用
   */
  const hasJumpedByPairRef = useRef({});

  /**
   * 会社・傷病ペアごとのアクティブボタン状態
   * タブ切り替え後に前回選択していたジャンプ種別を復元するために使用
   */
  const activeButtonByPairRef = useRef({});

  /**
   * ジャンプ処理中の到達待ち情報
   * ページ遷移完了前に途中ページを保存しないようにするために使用
   */
  const pendingJumpRef = useRef(null);

  /**
   * 直前の会社・傷病ペアキー
   * ペア切り替え発生時のみ復元処理を行うために使用
   */
  const previousPairKeyRef = useRef(null);

  /**
   * ページ状態を保存した直近の会社・傷病ペアキー
   * 初回表示直後のページを誤って保存しないようにするために使用
   */
  const lastPersistPairKeyRef = useRef(null);

  /**
   * 各セクションへのジャンプ可否
   * detailResultに該当ページ番号が存在するかで判定
   */
  const canJumpDisease = isJumpPageValid(detailResult, 'pages');
  const canJumpContents = isJumpPageValid(detailResult, 'tableOfContents');
  const canJumpLegend = isJumpPageValid(detailResult, 'legends');
  const canJumpAppendix = isJumpPageValid(detailResult, 'appendixTables');

  /**
   * 指定したボタンへのジャンプを実行済みか判定する
   *
   * @param {string} pairKey - 会社・傷病ペアキー
   * @param {string} buttonType - ボタン種別
   * @returns {boolean} ジャンプ実行済みの場合はtrue
   */
  const hasJumpedForButton = useCallback((pairKey, buttonType) => {
    if (!pairKey || !buttonType) return false;
    return !!hasJumpedByPairRef.current[pairKey]?.[buttonType];
  }, []);

  /**
   * 指定したボタンへのジャンプを実行済みとして記録する
   *
   * @param {string} pairKey - 会社・傷病ペアキー
   * @param {string} buttonType - ボタン種別
   */
  const markJumpedForButton = useCallback((pairKey, buttonType) => {
    if (!pairKey || !buttonType) return;
    const pairState = hasJumpedByPairRef.current[pairKey] ?? {};
    hasJumpedByPairRef.current[pairKey] = {
      ...pairState,
      [buttonType]: true,
    };
  }, []);

  /**
   * ボタン種別に応じた初期ジャンプ先ページを取得する
   *
   * @param {string} buttonType - ボタン種別
   * @returns {number|undefined} 初期ジャンプ先ページ番号
   */
  const getDefaultJumpPage = useCallback((buttonType) => {
    switch (buttonType) {
      case JUMP_BUTTON_TYPES.DISEASE:
        return detailResult?.pages;
      case JUMP_BUTTON_TYPES.CONTENTS:
        return detailResult?.tableOfContents;
      case JUMP_BUTTON_TYPES.LEGEND:
        return detailResult?.legends;
      case JUMP_BUTTON_TYPES.APPENDIX:
        return detailResult?.appendixTables;
      default:
        return undefined;
    }
  }, [detailResult]);

  /**
   * 会社・傷病ペアとボタン種別に紐づく保存済みページ番号を取得する
   *
   * @param {string} pairKey - 会社・傷病ペアキー
   * @param {string} buttonType - ボタン種別
   * @returns {number|undefined} 保存済みページ番号
   */
  const getSavedJumpPageForPair = useCallback((pairKey, buttonType) => {
    if (!pairKey || !buttonType) return undefined;
    return jumpPageStateByPairRef.current[pairKey]?.[buttonType];
  }, []);

  /**
   * 会社・傷病ペアとボタン種別に紐づくページ番号を保存する
   *
   * @param {string} pairKey - 会社・傷病ペアキー
   * @param {string} buttonType - ボタン種別
   * @param {number} page - 保存するページ番号
   */
  const saveJumpPageForPair = useCallback((pairKey, buttonType, page) => {
    if (!pairKey || !buttonType) return;
    if (!Number.isFinite(page) || page < 1) return;

    const pairState = jumpPageStateByPairRef.current[pairKey] ?? {};
    jumpPageStateByPairRef.current[pairKey] = {
      ...pairState,
      [buttonType]: page,
    };
  }, []);

  /**
   * 会社・傷病ペアごとのアクティブボタンを取得する
   *
   * @param {string} pairKey - 会社・傷病ペアキー
   * @returns {string} アクティブなボタン種別
   */
  const getActiveButtonForPair = useCallback((pairKey) => {
    if (!pairKey) return JUMP_BUTTON_TYPES.DISEASE;
    return activeButtonByPairRef.current[pairKey] ?? JUMP_BUTTON_TYPES.DISEASE;
  }, []);

  /**
   * 会社・傷病ペアごとのアクティブボタンを保存する
   *
   * @param {string} pairKey - 会社・傷病ペアキー
   * @param {string} buttonType - ボタン種別
   */
  const setActiveButtonForPair = useCallback((pairKey, buttonType) => {
    if (!pairKey || !buttonType) return;
    activeButtonByPairRef.current[pairKey] = buttonType;
  }, []);

  /**
   * ジャンプ処理中の到達待ち情報を設定する
   *
   * @param {string} pairKey - 会社・傷病ペアキー
   * @param {string} buttonType - ボタン種別
   * @param {number} targetPage - ジャンプ先ページ番号
   */
  const setPendingJump = useCallback((pairKey, buttonType, targetPage) => {
    if (!pairKey || !buttonType || !Number.isFinite(targetPage) || targetPage < 1) {
      pendingJumpRef.current = null;
      return;
    }
    pendingJumpRef.current = { pairKey, buttonType, targetPage };
  }, []);

  /**
   * アクティブボタンを更新し、現在の会社・傷病ペアにも保存する
   *
   * @param {string} buttonType - ボタン種別
   */
  const setActiveJumpButton = useCallback((buttonType) => {
    setActiveJumpButtonState(buttonType);
    setActiveButtonForPair(activePairKey, buttonType);
  }, [activePairKey, setActiveButtonForPair]);

  /**
   * ジャンプ実行の共通処理
   * ボタンを押すたびに各セクションの初期ページへ移動する
   *
   * @param {string} buttonType - ボタン種別
   * @param {number} defaultPage - 初期ジャンプ先ページ番号
   */
  const executeJump = useCallback((buttonType, defaultPage) => {
    if (!detailResult || !activePairKey) return;

    const targetPage = Number(defaultPage);
    if (!Number.isFinite(targetPage) || targetPage < 1) return;

    setActiveJumpButton(buttonType);
    markJumpedForButton(activePairKey, buttonType);
    saveJumpPageForPair(activePairKey, buttonType, targetPage);
    setPendingJump(activePairKey, buttonType, targetPage);
    jumpToPage(targetPage);
  }, [
    detailResult,
    activePairKey,
    setActiveJumpButton,
    markJumpedForButton,
    saveJumpPageForPair,
    setPendingJump,
    jumpToPage,
  ]);

  /** 該当傷病へジャンプ */
  const handleJumpDisease = useCallback(() => {
    executeJump(JUMP_BUTTON_TYPES.DISEASE, getDefaultJumpPage(JUMP_BUTTON_TYPES.DISEASE));
  }, [executeJump, getDefaultJumpPage]);

  /** 目次へジャンプ */
  const handleJumpContents = useCallback(() => {
    executeJump(JUMP_BUTTON_TYPES.CONTENTS, getDefaultJumpPage(JUMP_BUTTON_TYPES.CONTENTS));
  }, [executeJump, getDefaultJumpPage]);

  /** 凡例へジャンプ */
  const handleJumpLegends = useCallback(() => {
    executeJump(JUMP_BUTTON_TYPES.LEGEND, getDefaultJumpPage(JUMP_BUTTON_TYPES.LEGEND));
  }, [executeJump, getDefaultJumpPage]);

  /** 別表へジャンプ */
  const handleJumpAppendix = useCallback(() => {
    executeJump(JUMP_BUTTON_TYPES.APPENDIX, getDefaultJumpPage(JUMP_BUTTON_TYPES.APPENDIX));
  }, [executeJump, getDefaultJumpPage]);

  /**
   * Effect: 該当傷病ボタンの初期ページ状態を保存する
   * 会社・傷病ペアごとの初期表示ページを該当傷病の復元先として扱う
   */
  useEffect(() => {
    if (!activePairKey) return;

    const savedDiseasePage = getSavedJumpPageForPair(activePairKey, JUMP_BUTTON_TYPES.DISEASE);
    if (!Number.isFinite(savedDiseasePage) || savedDiseasePage < 1) {
      saveJumpPageForPair(activePairKey, JUMP_BUTTON_TYPES.DISEASE, currentPage);
    }

    markJumpedForButton(activePairKey, JUMP_BUTTON_TYPES.DISEASE);
  }, [activePairKey, currentPage, getSavedJumpPageForPair, saveJumpPageForPair, markJumpedForButton]);

  /**
   * Effect: 会社・傷病ペア切り替え時にジャンプボタン状態を復元する
   * 前回選択していたボタンが該当傷病以外の場合は、保存済みページへ自動ジャンプする
   */
  useEffect(() => {
    if (!activePairKey) return;

    const previousPairKey = previousPairKeyRef.current;
    const isPairSwitched = !!previousPairKey && previousPairKey !== activePairKey;
    previousPairKeyRef.current = activePairKey;

    const restoredButton = getActiveButtonForPair(activePairKey);
    setActiveJumpButtonState(restoredButton);

    if (!isPairSwitched) return;
    if (restoredButton === JUMP_BUTTON_TYPES.DISEASE) return;

    const savedPage = getSavedJumpPageForPair(activePairKey, restoredButton);
    const defaultPage = getDefaultJumpPage(restoredButton);
    const targetPage = Number.isFinite(savedPage) && savedPage > 0 ? savedPage : defaultPage;
    if (!Number.isFinite(targetPage) || targetPage < 1) return;

    markJumpedForButton(activePairKey, restoredButton);
    saveJumpPageForPair(activePairKey, restoredButton, targetPage);
    setPendingJump(activePairKey, restoredButton, targetPage);
    jumpToPage(targetPage);
  }, [
    activePairKey,
    getActiveButtonForPair,
    getSavedJumpPageForPair,
    getDefaultJumpPage,
    markJumpedForButton,
    saveJumpPageForPair,
    setPendingJump,
    jumpToPage,
  ]);

  /**
   * Effect: 現在のジャンプボタンに対応するページ番号を保存する
   * ジャンプ完了後の手動ページ移動も、次回同じボタンへ戻るための復元先として保持する
   */
  useEffect(() => {
    if (!activePairKey) return;

    if (lastPersistPairKeyRef.current !== activePairKey) {
      lastPersistPairKeyRef.current = activePairKey;
      return;
    }

    const buttonType = getActiveButtonForPair(activePairKey);
    if (!hasJumpedForButton(activePairKey, buttonType)) return;

    const pending = pendingJumpRef.current;
    if (pending && pending.pairKey === activePairKey && pending.buttonType === buttonType) {
      if (currentPage !== pending.targetPage) return;
      pendingJumpRef.current = null;
    }

    saveJumpPageForPair(activePairKey, buttonType, currentPage);
  }, [
    activePairKey,
    currentPage,
    getActiveButtonForPair,
    hasJumpedForButton,
    saveJumpPageForPair,
  ]);

  return {
    // 状態
    activeJumpButton,
    setActiveJumpButton,
    canJumpDisease,
    canJumpContents,
    canJumpLegend,
    canJumpAppendix,
    // ハンドラ
    handleJumpDisease,
    handleJumpContents,
    handleJumpLegends,
    handleJumpAppendix,
  };
};

export default useJumpButtonGroup;
